import { render, html } from "lit";
import { actions, dom } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { CellModel, CellSource, ColumnSource, RequestFilters, RowSource } from "@omegagrid/grid";
import rp, { GridEditorAdapter, GridEditorOptions } from "@reporting/rp-app-host";
import { ErrorEntity } from "@reporting/rp-types/core";

const PAGE_SIZE = 500;

const priorityColorMap: Record<string, string> = {
	'error': '#e20000ff',
	'exception': '#e20000ff',
	'access': '#B79900',
	'client': '#0078d4',
}

const priorityIconMap: Record<string, string> = {
	'error': 'circle-exclamation',
	'exception': 'triangle-exclamation',
	'access': 'do-not-enter',
	'client': 'user',
}

export class ErrorLogGridAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[];

	attached() {
		dom.appendStyle(this.component.table.shadowRoot, `
			a { color: var(--og-accent-color) !important; }
			a:hover { text-decoration: underline; cursor: pointer; }
		`, '__rp-super-error-log-grid-style');
	}

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;
		this._columns = [{
			data: 'date_created',
			header: msg('Date'),
			t: 'dt',
			w: 145,
		}, {
			data: 'message',
			header: msg('Text'),
			w: 650,
			renderer: this.messageRenderer
		}, {
			data: 'priority',
			header: msg('Type'),
			t: 's',
			content: [
				{ k: 'error', v: msg('Error') },
				{ k: 'exception', v: msg('Exception') },
				{ k: 'access', v: msg('Access') },
				{ k: 'client', v: msg('Client') },
			],
			w: 100,
		}, {
			data: 'url',
			header: msg('URL'),
			t: 't',
			w: 120,
		}, {
			data: 'domain',
			header: msg('Server'),
			t: 't',
			w: 120,
		}, {
			data: 'ip',
			header: `IP ${msg('address')}`,
			t: 't',
			w: 120,
		}, {
			data: 'user_agent',
			header: msg('User agent'),
			t: 't',
			w: 120,
		}, {
			data: 'user_name',
			header: msg('User'),
			t: 't',
			w: 120,
		}, {
			data: 'accounting',
			header: msg('Accounting'),
			t: 'n',
			w: 120,
		}, {
			data: 'has_content',
			t: 'b',
			hidden: true,
		}];
		return this._columns;
	}

	async getRows(filters?: RequestFilters): Promise<RowSource[]> {
		const logs = await rp.ms('server').get<ErrorEntity[]>(`/errors`, this.createRequestParams(filters, {
			count: PAGE_SIZE,
		}));
		return logs.map(data => ({ data: {
			...data,
			priority: this.createPriorityCell(data.priority),
		}}));
	}

	createPriorityCell(priority: string): Partial<CellSource> {
		return {
			t: 'h',
			v: `<span style="color: ${priorityColorMap[priority]}">
					<og-icon icon="${priorityIconMap[priority]}"></og-icon>
					${msg(priority)}
				</span>`
		}
	}

	messageRenderer = (div: HTMLDivElement, cell: CellModel) => {
		dom.empty(div);

		const innerDiv = dom.createElement<HTMLDivElement>('div');
		innerDiv.style.display = 'flex';

		const data = cell.row.data as ErrorEntity;
		render(html`
			${data.has_content ? html`
				<a @pointerdown="${(e: PointerEvent) => e.stopPropagation()}"
					@click="${() => actions.dispatchActionEvent(this.editor, 'editor', {
					key: 'click',
					value: data.idz,
				})}">${data.message}</a>
			` : data.message}
		`, innerDiv);

		div.prepend(innerDiv);
		return div;
	}

	getOptions(): Partial<GridEditorOptions> {
		return this.createOptions({
			colLetters: false,
			columnSorting: true,
			filters: true,
			pageSize: PAGE_SIZE,
			editable: false,
			lightTheme: null,
			darkTheme: null,
			toolbar: {
				autoUpdate: {enabled: true},
				save: {enabled: false},
				discard: {enabled: false},
				new: {enabled: false},
			},
		});
	}

}
